import { CircularProgress, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@material-ui/core';
import { Visibility, Autorenew } from '@material-ui/icons';
import { MoreHoriz } from '@material-ui/icons';
import { Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import Spin from 'components/Spin';
import Truncate from 'components/Truncate';
import React from 'react';
import { toast } from 'react-toastify';
import { dateFormat } from 'v2/formatters/date';
import { InvoiceStatus, SubscriptionStatus } from 'v2/interfaces/webhook2';
import webhookService from 'v2/services/webhook2';

import type { WebhookHistory } from 'v2/interfaces/webhook2';

import { useWebhookHistoryContext } from './context';
import { QuickView } from './Quickview';
import styles from '../styles';

const messageStatus = {
  SUCCESS: 'Enviado',
  PENDING: 'Pendente',
  ERROR: 'Retornou erro',
} as Record<WebhookHistory['status'], string>;

export const ListWebhookHistory = () => {
  const classes = styles();

  const [submitingResend, setSubmitingResend] = React.useState<boolean>(false);
  const [openQuickview, setOpenQuickview] = React.useState<boolean>(false);
  const [currentWebhookHistory, setCurrentWebhookHistory] = React.useState<WebhookHistory>();

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement>();

  const { updateList, listWebhookHistory, webhook, loading, type, productId, pagination, setPagination } = useWebhookHistoryContext();

  const handleOpenMenuActions = (event: React.MouseEvent<HTMLButtonElement>, item: WebhookHistory) => {
    setAnchorEl(event.currentTarget);
    setCurrentWebhookHistory(item);
  };

  const handleCloseMenuActions = () => {
    setAnchorEl(undefined);
    setCurrentWebhookHistory(undefined);
  };

  const handleOpenQuickview = () => {
    setAnchorEl(undefined);
    setOpenQuickview(true);
  };

  const handleCloseQuickview = () => setOpenQuickview(!openQuickview);

  const handleResendWebhook = async () => {
    setSubmitingResend(true);

    try {
      await webhookService.resendUrl(currentWebhookHistory?._id);
      setAnchorEl(undefined);
      toast.success('Webhook reenviado com sucesso');
      setSubmitingResend(false);
      updateList();
    } catch (e) {
      toast.error('Ocorreu um erro ao reenviar webhook');
      setSubmitingResend(false);
    }
  };

  const shouldRenderProductColumn = productId === -1;
  const disabledButtonResend = currentWebhookHistory?.status === 'pending';

  if (loading) {
    return <Spin />;
  }

  if (listWebhookHistory?.length === 0) {
    return (
      <Typography className={classes.notFound} color='inherit'>
        Nenhum histórico de webhook encontrado...
      </Typography>
    );
  }

  return (
    <>
      <TableContainer>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell width={160}>Status</TableCell>
              {type !== 'abandonment' && <TableCell>Fatura/Contrato</TableCell>}
              {shouldRenderProductColumn && <TableCell>Produto</TableCell>}
              <TableCell>Comprador</TableCell>
              <TableCell width={140}>Data de envio</TableCell>
              <TableCell width={120} align='center'>
                Retorno HTTP
              </TableCell>
              <TableCell width={30}></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {listWebhookHistory?.map((item) => {
              const error = item.status !== 'success' && item.status !== 'pending';
              const isPending = item.status === 'pending';
              const messageState = ['success', 'pending'].includes(item.status) ? item.status.toUpperCase() : 'ERROR';
              const hasProduct = item.product.id !== -1;
              const classNameStatus = error ? classes.rowError : isPending ? classes.rowPending : null;
              const httpStatus = item.lastSend?.returnStatus;

              return (
                <TableRow key={item._id} className={classNameStatus}>
                  <TableCell>{messageStatus[messageState]}</TableCell>

                  {item.type === 'invoice' && (
                    <TableCell>
                      <div>{item.entityId}</div>
                      <div className={classes.invoice}>
                        <span>{InvoiceStatus[item.entityStatus] ?? '-'}</span>
                      </div>
                    </TableCell>
                  )}

                  {item.type === 'contract' && (
                    <TableCell>
                      <div>{item.entityId}</div>
                      <div className={`${classes.subscription} ${item.entityStatus}`}>
                        <span>{SubscriptionStatus[item.entityStatus]}</span>
                      </div>
                    </TableCell>
                  )}

                  {shouldRenderProductColumn && (
                    <TableCell>
                      {hasProduct && (
                        <>
                          <Truncate count={30} final='...' title={item.product.title}>
                            {item.product.title}
                          </Truncate>
                          {item.product.id}
                        </>
                      )}

                      {!hasProduct && '-'}
                    </TableCell>
                  )}

                  <TableCell>
                    <Truncate count={25} final='...' title={item.customer.name}>
                      {item.customer.name}
                    </Truncate>
                    <Truncate count={35} final='...' title={item.customer.email}>
                      {item.customer.email}
                    </Truncate>
                  </TableCell>

                  <TableCell>
                    {!isPending && item.sent_at && (
                      <>
                        {dateFormat(item.sent_at, 'dd/MM/yyyy')} <br />
                        {dateFormat(item.sent_at, ' HH:mm:ss')}
                      </>
                    )}

                    {(isPending || !item.sent_at) && '-'}
                  </TableCell>

                  <TableCell align='center'>
                    {httpStatus && <span className={`${classes.webhookStatus} --status-${item.status}`}>{httpStatus}</span>}
                    {!item.lastSend?.returnStatus && '-'}
                  </TableCell>

                  <TableCell>
                    <IconButton onClick={(e) => handleOpenMenuActions(e, item)}>
                      <MoreHoriz />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[10, 20, 40]}
          component='div'
          size='small'
          labelRowsPerPage='Itens por página:'
          count={pagination?.total_items || 0}
          rowsPerPage={pagination?.per_page || 10}
          page={(pagination?.current_page || 1) - 1}
          onPageChange={(_: unknown, page: number) => setPagination({ ...pagination, ...{ current_page: page + 1 } })}
          onRowsPerPageChange={(e) => setPagination({ ...pagination, ...{ per_page: Number(e.target?.value), current_page: 1 } })}
        />
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        id='list-webhook-history-menu-actions'
        open={!!anchorEl}
        getContentAnchorEl={null}
        onClose={handleCloseMenuActions}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleResendWebhook} disabled={disabledButtonResend}>
          <ListItemIcon className={classes.listHistoryAction}>{submitingResend ? <CircularProgress size={22} /> : <Autorenew />}</ListItemIcon>
          <ListItemText primary='Reenviar webhook' />
        </MenuItem>

        <MenuItem onClick={handleOpenQuickview}>
          <ListItemIcon className={classes.listHistoryAction}>
            <Visibility />
          </ListItemIcon>
          <ListItemText primary='Visualizar detalhes' />
        </MenuItem>
      </Menu>

      <QuickView open={openQuickview} url={webhook} webhook={currentWebhookHistory} handleClose={handleCloseQuickview} />
    </>
  );
};
